import { useEffect, useState } from "react";
import {
  Users,
  CheckCircle2,
  Radio,
  Star,
  MapPin,
  TrendingUp,
} from "lucide-react";
import api from "../services/api";

// One key metric, shown as a clean card with an icon chip and a small delta.
function HighlightCard({ icon, label, value, delta, tint, loading }) {
  const Icon = icon;
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span
          className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${tint}`}
        >
          <Icon className="w-5 h-5" />
        </span>
        {delta ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <TrendingUp className="w-3.5 h-3.5" />
            {delta}
          </span>
        ) : null}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
          {loading ? "—" : value}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function RatingBar({ star, count, total }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-8 text-gray-500 dark:text-gray-400 flex items-center gap-0.5">
        {star}
        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
      </span>
      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div
          className="h-full rounded-full bg-amber-400"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-gray-500 dark:text-gray-400">
        {count}
      </span>
    </div>
  );
}

/**
 * "Data highlights" strip for the dashboard: onboarding + ride volume cards,
 * the most popular pickup points, and the overall ride-rating breakdown.
 * Pulls from /admin/highlights and refreshes on a light poll.
 */
function DashboardHighlights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [placeTab, setPlaceTab] = useState("pickups"); // "pickups" | "destinations"

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get("/v1/admin/highlights");
        if (alive) {
          setData(res.data);
          setError(false);
        }
      } catch {
        // Last-known data stays on screen; the banner says it is stale.
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [reloadKey]);

  // Never show 0 for a number we don't have.
  const count = (value) => (typeof value === "number" ? value.toLocaleString() : "—");
  const noData = loading || !data;

  const customers = data?.customers || {};
  const riders = data?.riders || {};
  const rides = data?.rides || {};
  const ratings = data?.ratings || { average: null, count: null, distribution: {} };
  const places =
    (placeTab === "pickups"
      ? data?.popularPickups
      : data?.popularDestinations) || [];
  const maxPlace = places.reduce((m, p) => Math.max(m, p.count), 0) || 1;

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {data
            ? "Couldn't refresh the highlights. Showing the last loaded figures. "
            : "Couldn't load the highlights. "}
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              setReloadKey((k) => k + 1);
            }}
            className="underline font-medium"
          >
            Retry
          </button>
        </div>
      ) : null}
      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <HighlightCard
          icon={Users}
          label="Passengers onboarded"
          value={count(customers.total)}
          delta={customers.onboarded7d ? `+${customers.onboarded7d} this week` : null}
          tint="bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300"
          loading={noData}
        />
        <HighlightCard
          icon={CheckCircle2}
          label="Rides completed"
          value={count(rides.completed)}
          delta={rides.completed7d ? `+${rides.completed7d} this week` : null}
          tint="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300"
          loading={noData}
        />
        <HighlightCard
          icon={Radio}
          label="Riders online now"
          value={`${count(riders.online)} / ${count(riders.total)}`}
          tint="bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300"
          loading={noData}
        />
        <HighlightCard
          icon={Star}
          label={`Overall rating · ${count(ratings.count)} rated`}
          value={ratings.average ? ratings.average.toFixed(2) : "—"}
          tint="bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300"
          loading={noData}
        />
      </div>

      {/* Popular pickups + rating distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#FF8C00]" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Popular {placeTab === "pickups" ? "pickup" : "drop-off"} points
              </h3>
            </div>
            {/* Pickups / Destinations toggle */}
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5">
              {[
                { key: "pickups", label: "Pickups" },
                { key: "destinations", label: "Destinations" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setPlaceTab(t.key)}
                  className={`text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${
                    placeTab === t.key
                      ? "bg-white dark:bg-gray-700 text-[#FF8C00] shadow-sm"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {noData ? (
            <p className="text-sm text-gray-400">—</p>
          ) : places.length === 0 ? (
            <p className="text-sm text-gray-400">
              No {placeTab === "pickups" ? "pickup" : "destination"} data yet.
            </p>
          ) : (
            <div className="space-y-3">
              {places.map((p) => (
                <div key={p.name} className="text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-700 dark:text-gray-300 truncate pr-2">
                      {p.name}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 shrink-0">
                      {p.count}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#FF8C00]"
                      style={{ width: `${Math.round((p.count / maxPlace) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Ride ratings
              </h3>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {ratings.average ? ratings.average.toFixed(2) : "—"}
            </span>
          </div>
          <div className="space-y-2">
            {noData ? <p className="text-sm text-gray-400">—</p> : [5, 4, 3, 2, 1].map((s) => (
              <RatingBar
                key={s}
                star={s}
                count={ratings.distribution?.[s] || 0}
                total={ratings.count || 0}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardHighlights;
